import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { formatINR, stripCommas } from '../lib/numberFormat';

const AddTransactionDialog = ({ 
  onSuccess, 
  onClose,
  onSymbolChange,  // Callback when symbol changes - used to highlight stock in parent
  isOpen: controlledOpen,
  buttonText = "Add Transaction", 
  buttonVariant = "primary", 
  buttonSize = "default",
  showTrigger = true,
  defaultTransactionType = 'buy_primary',
  defaultSymbol = ''
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOpen !== undefined ? (val) => { if (!val && onClose) onClose(); } : setInternalOpen;
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [mtfAccounts, setMTFAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    member_id: '',
    transaction_type: defaultTransactionType,
    symbol: '',
    quantity: 0,
    price: 0,
    transaction_date: new Date().toISOString().split('T')[0],
    purchase_type: 'Primary',
    mtf_account_id: '',
    demat_account_id: '',
  });

  // Symbol autocomplete state
  const [symbolQuery, setSymbolQuery] = useState('');
  const [symbolSuggestions, setSymbolSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingSymbols, setSearchingSymbols] = useState(false);
  const symbolInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchData();
      setFormData(prev => ({ 
        ...prev, 
        transaction_type: defaultTransactionType,
        symbol: defaultSymbol || prev.symbol
      }));
      if (defaultSymbol) {
        setSymbolQuery(defaultSymbol);
      }
    }
  }, [open, defaultTransactionType, defaultSymbol]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        symbolInputRef.current && 
        !symbolInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced symbol search
  useEffect(() => {
    const searchSymbols = async () => {
      if (!symbolQuery || symbolQuery.length < 1) {
        setSymbolSuggestions([]);
        return;
      }

      setSearchingSymbols(true);
      try {
        const res = await api.get(`/symbols/search?q=${encodeURIComponent(symbolQuery)}`);
        setSymbolSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Symbol search failed');
      } finally {
        setSearchingSymbols(false);
      }
    };

    const debounceTimer = setTimeout(searchSymbols, 300);
    return () => clearTimeout(debounceTimer);
  }, [symbolQuery]);

  const fetchData = async () => {
    try {
      const [membersRes, mtfRes] = await Promise.all([
        api.get('/family-members'),
        api.get('/mtf-accounts')
      ]);
      setMembers(membersRes.data || []);
      setMTFAccounts(mtfRes.data || []);
    } catch (err) {
      console.error('Failed to load form data');
    }
  };

  const handleSymbolInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setSymbolQuery(value);
    setFormData({ ...formData, symbol: value });
  };

  const selectSymbol = async (symbol) => {
    setFormData(prev => ({ ...prev, symbol }));
    setSymbolQuery(symbol);
    setShowSuggestions(false);
    
    // Notify parent about symbol change (for highlighting in table)
    if (onSymbolChange) {
      onSymbolChange(symbol);
    }
    
    // If member is already selected and transaction type requires holdings data, fetch it
    if (formData.member_id && isHoldingsBasedTransaction(formData.transaction_type)) {
      await fetchHoldingsForSymbol(symbol, formData.member_id);
    }
  };

  // Check if transaction type requires pre-filling from holdings
  const isHoldingsBasedTransaction = (txType) => {
    return ['sell', 'las_pledge', 'las_unpledge'].includes(txType);
  };

  // Fetch holdings when member is selected (for sell/pledge/unpledge transactions)
  const fetchHoldingsForSymbol = async (symbol, memberId) => {
    if (!symbol || !memberId) return;
    
    try {
      const res = await api.get(`/holdings?member_id=${memberId}`);
      const holdings = res.data || [];
      const matchingHolding = holdings.find(h => 
        h.symbol === symbol || h.display_name === symbol
      );
      
      if (matchingHolding) {
        const currentPrice = matchingHolding.custom_fields?.current_price || matchingHolding.purchase_price || 0;
        // Round price to 2 decimal places
        const roundedPrice = Math.round(currentPrice * 100) / 100;
        setFormData(prev => ({
          ...prev,
          quantity: matchingHolding.quantity || 0,
          price: roundedPrice,
          // Store holding info for pledge status check
          _holdingPledged: matchingHolding.pledged || false,
          _holdingId: matchingHolding.id
        }));
        
        // Show warning if trying to sell pledged shares
        if (matchingHolding.pledged && formData.transaction_type === 'sell') {
          toast.info('Note: These shares are currently pledged. Selling will automatically unpledge them first.');
        }
      }
    } catch (err) {
      console.log('Could not fetch holdings for pre-fill:', err);
    }
  };

  // Handle member change - fetch holdings if symbol is already selected
  const handleMemberChange = async (memberId) => {
    setFormData(prev => ({ ...prev, member_id: memberId }));
    
    // If symbol is already selected and this is a holdings-based transaction, fetch holdings
    if (formData.symbol && isHoldingsBasedTransaction(formData.transaction_type)) {
      await fetchHoldingsForSymbol(formData.symbol, memberId);
    }
  };

  // Handle transaction type change - fetch holdings if switching to sell/pledge/unpledge
  const handleTransactionTypeChange = async (txType) => {
    setFormData(prev => ({ ...prev, transaction_type: txType, mtf_account_id: '' }));
    
    // If symbol and member are already selected and switching to holdings-based transaction
    if (formData.symbol && formData.member_id && isHoldingsBasedTransaction(txType)) {
      await fetchHoldingsForSymbol(formData.symbol, formData.member_id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Map frontend transaction types to backend expected values
      let backendTransactionType = 'buy';
      let purchaseType = 'Primary';
      
      if (formData.transaction_type === 'sell') {
        backendTransactionType = 'sell';
        purchaseType = null;
      } else if (formData.transaction_type === 'buy_primary') {
        backendTransactionType = 'buy';
        purchaseType = 'Primary';
      } else if (formData.transaction_type === 'buy_secondary') {
        backendTransactionType = 'buy';
        purchaseType = 'Secondary';
      } else if (formData.transaction_type === 'buy_mtf') {
        backendTransactionType = 'buy';
        purchaseType = 'MTF';
      } else if (formData.transaction_type === 'las_pledge' || formData.transaction_type === 'las_unpledge') {
        backendTransactionType = formData.transaction_type;
        purchaseType = null;
      }
      
      const payload = {
        member_id: formData.member_id,
        transaction_type: backendTransactionType,
        symbol: formData.symbol,
        quantity: parseInt(formData.quantity),
        price: parseFloat(formData.price),
        transaction_date: formData.transaction_date,
        purchase_type: purchaseType,
        mtf_account_id: formData.transaction_type === 'buy_mtf' ? formData.mtf_account_id : null,
      };

      const res = await api.post('/transactions', payload);
      const result = res.data?.result || {};
      
      if (backendTransactionType === 'sell' && result.realized_pnl !== undefined) {
        const pnl = result.realized_pnl;
        const pnlFormatted = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Math.abs(pnl));
        if (pnl >= 0) {
          toast.success(`Sold ${formData.quantity} ${formData.symbol} — Profit: ${pnlFormatted}`);
        } else {
          toast.error(`Sold ${formData.quantity} ${formData.symbol} — Loss: ${pnlFormatted}`);
        }
        if (result.warning) {
          toast.warning(result.warning);
        }
      } else {
        toast.success('Transaction recorded successfully!');
      }
      
      // Reset form
      setFormData({
        member_id: '',
        transaction_type: 'buy_primary',
        symbol: '',
        quantity: 0,
        price: 0,
        transaction_date: new Date().toISOString().split('T')[0],
        purchase_type: 'Primary',
        mtf_account_id: '',
        demat_account_id: '',
      });
      setSymbolQuery('');
      setOpen(false);
      
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record transaction');
    } finally {
      setLoading(false);
    }
  };

  const buttonClasses = buttonVariant === 'primary' 
    ? 'btn-primary flex items-center justify-center gap-2' 
    : 'btn-secondary flex items-center justify-center gap-2';

  const dialogContent = (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Record Transaction</DialogTitle>
      </DialogHeader>
      <div className="mb-2 -mt-1">
        <button
          type="button"
          onClick={() => { setOpen(false); navigate('/bulk-import'); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition"
          data-testid="open-bulk-ai-from-dialog"
        >
          <Sparkles size={13} /> Have multiple trades? Paste broker update / CSV instead
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Member *</label>
          <select
            className="input-field w-full"
            value={formData.member_id}
            onChange={(e) => handleMemberChange(e.target.value)}
            required
          >
            <option value="">Select Member</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.relation})
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Type *</label>
          <select
            className="input-field w-full"
            value={formData.transaction_type}
            onChange={(e) => handleTransactionTypeChange(e.target.value)}
          >
            <option value="buy_primary">Buy Primary (Direct Purchase)</option>
            <option value="buy_secondary">Buy Secondary (Secondary Market)</option>
            <option value="buy_mtf">Buy (MTF)</option>
            <option value="sell">Sell</option>
            <option value="las_pledge">LAS Transfer - Pledge</option>
            <option value="las_unpledge">LAS Transfer - Unpledge</option>
          </select>
          {formData._holdingPledged && formData.transaction_type === 'sell' && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              ⚠️ These shares are pledged. They will be auto-unpledged before selling.
            </p>
          )}
        </div>
        
        {formData.transaction_type === 'buy_mtf' && (
          <div>
            <label className="block text-sm font-medium mb-2">MTF Account *</label>
            <select
              className="input-field w-full"
              value={formData.mtf_account_id}
              onChange={(e) => setFormData({ ...formData, mtf_account_id: e.target.value })}
              required
            >
              <option value="">Select MTF Account</option>
              {mtfAccounts.map((a) => {
                const member = members.find(m => m.id === a.member_id);
                return (
                  <option key={a.id} value={a.id}>
                    {a.bank_name} - {member?.name || 'Unknown'} ({a.account_number})
                  </option>
                );
              })}
            </select>
            {mtfAccounts.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No MTF accounts found. Create one in the Accounts section first.
              </p>
            )}
          </div>
        )}
        
        {/* Symbol input with autocomplete */}
        <div className="relative">
          <label className="block text-sm font-medium mb-2">Symbol *</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={16} />
            <input
              ref={symbolInputRef}
              type="text"
              className="input-field w-full"
              style={{ paddingLeft: '2.5rem' }}
              placeholder="Start typing... e.g., REL, HDFC, TCS"
              value={symbolQuery || formData.symbol}
              onChange={handleSymbolInputChange}
              onFocus={() => symbolQuery && setShowSuggestions(true)}
              autoComplete="off"
              required
            />
            {searchingSymbols && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
          
          {/* Autocomplete dropdown */}
          {showSuggestions && symbolSuggestions.length > 0 && (
            <div 
              ref={suggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              {symbolSuggestions.map((sym, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => selectSymbol(sym.symbol)}
                >
                  <span className="font-medium">{sym.symbol}</span>
                  {sym.name && <span className="text-gray-500 ml-2">- {sym.name}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Quantity *</label>
            <input
              type="number"
              className="input-field w-full"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              min="0"
              step="1"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Price *</label>
            <input
              type="text" inputMode="decimal"
              className="input-field w-full"
              value={formatINR(formData.price)}
              onChange={(e) => setFormData({ ...formData, price: stripCommas(e.target.value) })}
              min="0"
             
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Date *</label>
          <input
            type="date"
            className="input-field w-full"
            value={formData.transaction_date}
            onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
            required
          />
        </div>
        
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Recording...' : 'Record Transaction'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );

  // If controlled externally (no trigger button needed)
  if (!showTrigger && controlledOpen !== undefined) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen && onClose) onClose();
      }}>
        {dialogContent}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) {
        setSymbolQuery('');
        setSymbolSuggestions([]);
        setShowSuggestions(false);
        if (onClose) onClose();
      }
    }}>
      <DialogTrigger asChild>
        <Button className={buttonClasses} size={buttonSize}>
          <Plus size={18} />
          {buttonText}
        </Button>
      </DialogTrigger>
      {dialogContent}
    </Dialog>
  );
};

export default AddTransactionDialog;
